/**
 * @description
 * Active filters display component showing currently applied filters.
 * Displays time range, batch size, and column filters in a clear, visual format.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 * @see admin/src/frontend/components/vscode-lens/ActiveFilters.test.tsx
 */

import { Filter, X, Clock, Hash, Columns } from 'lucide-react';
import type { FilterConfig } from '../../../types/vscode-lens';

interface ActiveFiltersProps {
  filters: FilterConfig;
  onRemoveColumnFilter?: (key: string) => void;
}

/**
 * Get human-readable label for time preset
 */
function getTimePresetLabel(preset: string): string {
  const labels: Record<string, string> = {
    lastDay: 'Last Day',
    lastWeek: 'Last Week',
    lastMonth: 'Last Month',
    custom: 'Custom Range',
  };
  return labels[preset] || preset;
}

/**
 * Format date for display
 */
function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
}

/**
 * Active filters display component
 */
export function ActiveFilters({ filters, onRemoveColumnFilter }: ActiveFiltersProps) {
  const hasColumnFilters = filters.columnFilters && Object.keys(filters.columnFilters).length > 0;
  const hasAnyFilters = filters.timePreset || filters.batchSize || hasColumnFilters;

  if (!hasAnyFilters) {
    return null;
  }

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-medium">Active Filters</h3>
      </div>

      <div className="flex flex-wrap gap-2">
        {/* Time Range Filter */}
        {filters.timePreset && (
          <div className="inline-flex items-center gap-1.5 rounded-md bg-blue-100 dark:bg-blue-950 px-2.5 py-1 text-xs font-medium text-blue-700 dark:text-blue-300">
            <Clock className="h-3 w-3" />
            <span>{getTimePresetLabel(filters.timePreset)}</span>
            {filters.timePreset === 'custom' && filters.startTime && filters.endTime && (
              <span className="text-blue-600 dark:text-blue-400">
                ({formatDate(filters.startTime)} - {formatDate(filters.endTime)})
              </span>
            )}
          </div>
        )}

        {/* Batch Size Filter */}
        {filters.batchSize && (
          <div className="inline-flex items-center gap-1.5 rounded-md bg-purple-100 dark:bg-purple-950 px-2.5 py-1 text-xs font-medium text-purple-700 dark:text-purple-300">
            <Hash className="h-3 w-3" />
            <span>Batch: {filters.batchSize}</span>
          </div>
        )}

        {/* Column Filters */}
        {hasColumnFilters && (
          <>
            {Object.entries(filters.columnFilters!).map(([key, value]) => {
              if (!value) return null;
              
              return (
                <div
                  key={key}
                  className="inline-flex items-center gap-1.5 rounded-md bg-green-100 dark:bg-green-950 px-2.5 py-1 text-xs font-medium text-green-700 dark:text-green-300"
                >
                  <Columns className="h-3 w-3" />
                  <span>
                    {key.replace(/_/g, ' ')}: {value}
                  </span>
                  {onRemoveColumnFilter && (
                    <button
                      onClick={() => onRemoveColumnFilter(key)}
                      className="ml-1 hover:text-green-900 dark:hover:text-green-100 transition-colors"
                      aria-label={`Remove ${key} filter`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Filter Count Summary */}
      <div className="mt-2 text-xs text-muted-foreground">
        {Object.keys(filters.columnFilters || {}).filter(k => filters.columnFilters![k]).length > 0 && (
          <span>
            {Object.keys(filters.columnFilters || {}).filter(k => filters.columnFilters![k]).length} column filter(s) active
          </span>
        )}
      </div>
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */